import React, { Fragment, useMemo } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  DEMO_DURATION_MS,
  formatDemoClock,
  loadDemoResult,
} from "./demoSession";

import "./Demo.css";

function MetricRing({ label, value, caption, color }) {
  const size = 96;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safe = Math.min(100, Math.max(0, Math.round(value || 0)));
  const offset = circumference - (safe / 100) * circumference;

  return (
    <div className="demo-result__metric">
      <p className="demo-result__metric-label">{label}</p>
      <div className="demo-result__ring">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeDashoffset={offset}
            strokeLinecap="round"
            fill="none"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </svg>
        <strong>{safe}%</strong>
      </div>
      <p className="demo-result__metric-caption">{caption}</p>
    </div>
  );
}

const DemoResult = () => {
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated } = useSelector((state) => state.auth || {});

  const result = useMemo(() => {
    return location.state && location.state.questions ? location.state : loadDemoResult();
  }, [location.state]);

  if (!result || !Array.isArray(result.questions) || result.questions.length === 0) {
    return (
      <div className="demo-page demo-page--compact">
        <div className="demo-page__card demo-page__empty">
          <h2>No demo result to show</h2>
          <p>Start a demo exam to see your score here.</p>
          <div className="demo-page__actions">
            <Link className="demo-page__btn demo-page__btn--green" to="/demo">
              Try a Demo
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { questions, selectedAnswers = {}, score = 0 } = result;
  const total = questions.length || 1;
  const answeredCount = Object.keys(selectedAnswers).length;
  const progress = Math.round((answeredCount / total) * 100);
  const accuracy = Math.round((score / total) * 100);
  const timeUsedMs = result.timeUsedMs || 0;
  const totalDurationMs = result.totalDurationMs || DEMO_DURATION_MS;
  const timeUsedPercent = Math.min(
    100,
    Math.round((timeUsedMs / totalDurationMs) * 100)
  );
  const percentile = Math.max(10, Math.min(95, accuracy - 2));

  const subscribePath = isAuthenticated ? "/my-subscriptions" : "/register";

  return (
    <Fragment>
      <div className="demo-page">
        <div className="demo-page__inner">
          <div className="demo-page__hero demo-page__hero--result">
            <p className="demo-page__kicker">Demo Performance · Study Mode</p>
            <h1 className="demo-page__title">
              You scored {score}/{total}
            </h1>
            <p className="demo-page__lead">
              This demo shows your score only. Correct answers, explanations and
              topic breakdown stay locked until you subscribe — the same study
              mode as the mobile app.
            </p>
          </div>

          <div className="demo-page__card">
            <div className="demo-result__metrics">
              <MetricRing
                label="Progress"
                value={progress}
                caption={`${answeredCount} of ${total} attempted`}
                color="#006b3f"
              />
              <MetricRing
                label="Accuracy"
                value={accuracy}
                caption="Visible score"
                color="#7c3aed"
              />
              <MetricRing
                label="Time Used"
                value={timeUsedPercent}
                caption={`${formatDemoClock(timeUsedMs)} of ${formatDemoClock(
                  totalDurationMs
                )}`}
                color="#2563eb"
              />
            </div>
            <p className="demo-result__time-note">
              Time summary: {formatDemoClock(timeUsedMs)} used out of{" "}
              {formatDemoClock(totalDurationMs)}
            </p>
          </div>

          <div className="demo-page__card demo-result__locked">
            <p className="demo-result__locked-title">
              You performed better than {percentile}% of students
            </p>
            <div className="demo-result__locked-banner">
              <i className="fas fa-lock" /> Detailed performance locked
            </div>
            <p className="demo-result__locked-copy">
              Subscribe to unlock full analysis, corrections, and topic breakdown.
            </p>
          </div>

          <div className="demo-page__card demo-result__locked">
            <h2 className="demo-page__card-title">Answer review locked</h2>
            <div className="demo-result__locked-preview" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <p className="demo-result__locked-copy">
              Correct answers and explanations are hidden in study mode. Unlock a
              plan to see where you went wrong and how to fix it.
            </p>
          </div>

          <div className="demo-page__card demo-result__locked">
            <h2 className="demo-page__card-title">Weak topic detected</h2>
            <div className="demo-result__locked-preview" aria-hidden="true">
              <span />
              <span />
            </div>
            <p className="demo-result__locked-copy">
              Unlock full analysis to see the detailed topic breakdown.
            </p>
          </div>

          <div className="demo-page__card">
            <h2 className="demo-page__card-title">Where you need improvement</h2>
            <ul className="demo-page__list">
              <li>
                <i className="fas fa-exclamation-circle" /> Weak topic mastery is
                reducing your total score.
              </li>
              <li>
                <i className="fas fa-exclamation-circle" /> Without structured
                guidance, improvement may be slow.
              </li>
              <li>
                <i className="fas fa-exclamation-circle" /> Subscribe to unlock
                corrections, explanations, and targeted practice.
              </li>
            </ul>
            <div className="demo-page__actions" style={{ marginBottom: 0 }}>
              <Link className="demo-page__btn demo-page__btn--green" to={subscribePath}>
                <i className="fas fa-unlock-alt" /> Unlock My Full Score
              </Link>
              <button
                type="button"
                className="demo-page__btn demo-page__btn--ghost"
                onClick={() => history.push("/demo/quiz")}
              >
                <i className="fas fa-redo" /> Retake Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default DemoResult;
